import { DashboardConfigurationZoneDirectionEnum } from "@repo/schema";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  type LucideIcon,
} from "lucide-react";
import { tKey, type I18nKey } from "@/core/i18n";
import React from "react";
import { useTranslation } from "react-i18next";

export const isHorizontalStrip = (
  direction: DashboardConfigurationZoneDirectionEnum,
): boolean =>
  direction === DashboardConfigurationZoneDirectionEnum.TOP_TO_BOTTOM ||
  direction === DashboardConfigurationZoneDirectionEnum.BOTTOM_TO_TOP;

export interface SafeBounds {
  safeStartPct: number;
  safeEndPct: number;
}

export const centerThicknessToSafeBounds = (
  centerPct: number,
  thicknessPct: number,
): SafeBounds => {
  const half = thicknessPct / 2;
  return {
    safeStartPct: centerPct - half,
    safeEndPct: 100 - (centerPct + half),
  };
};

export const safeBoundsToCenterThickness = (
  safeStartPct: number,
  safeEndPct: number,
): { centerPct: number; thicknessPct: number } => {
  const thicknessPct = 100 - safeStartPct - safeEndPct;
  const centerPct = safeStartPct + thicknessPct / 2;
  return { centerPct, thicknessPct };
};

/** Catalog keys for the two safe-zone inputs; render them with `t()`. */
export const safeZoneLabels = (
  direction: DashboardConfigurationZoneDirectionEnum,
): { start: I18nKey<"dashboard">; end: I18nKey<"dashboard"> } =>
  isHorizontalStrip(direction)
    ? {
        start: tKey("dashboard:zone.safeFromTop"),
        end: tKey("dashboard:zone.safeFromBottom"),
      }
    : {
        start: tKey("dashboard:zone.safeFromLeft"),
        end: tKey("dashboard:zone.safeFromRight"),
      };

const clampPct = (pct: number): number => Math.max(0, Math.min(100, pct));

const formatPct = (pct: number): string => `${Math.round(pct)}%`;

// A hazard hatch rather than a flat wash: the stripes stay readable over a dark
// frame and a bright one alike, where a single tint sinks into either.
const safeZoneHatch =
  "repeating-linear-gradient(135deg,rgba(214,52,68,0.22) 0px," +
  "rgba(214,52,68,0.22) 7px,transparent 7px,transparent 16px)";

const safeZoneBaseStyle: React.CSSProperties = {
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  background: `rgba(214,52,68,0.28) ${safeZoneHatch}`,
  pointerEvents: "none",
};

// White, not red — the divider has to hold the eye against the hatch behind it.
const innerDashed = "1px dashed rgba(255,255,255,0.55)";

export const getSafeZoneStyles = (
  direction: DashboardConfigurationZoneDirectionEnum,
  safeStartPct: number,
  safeEndPct: number,
): { start: React.CSSProperties; end: React.CSSProperties } => {
  const start = clampPct(safeStartPct);
  const end = clampPct(safeEndPct);

  if (isHorizontalStrip(direction)) {
    return {
      start: {
        ...safeZoneBaseStyle,
        left: 0,
        width: "100%",
        top: 0,
        height: `${start}%`,
        borderBottom: start > 0 ? innerDashed : undefined,
      },
      end: {
        ...safeZoneBaseStyle,
        left: 0,
        width: "100%",
        bottom: 0,
        height: `${end}%`,
        borderTop: end > 0 ? innerDashed : undefined,
      },
    };
  }

  return {
    start: {
      ...safeZoneBaseStyle,
      top: 0,
      height: "100%",
      left: 0,
      width: `${start}%`,
      borderRight: start > 0 ? innerDashed : undefined,
    },
    end: {
      ...safeZoneBaseStyle,
      top: 0,
      height: "100%",
      right: 0,
      width: `${end}%`,
      borderLeft: end > 0 ? innerDashed : undefined,
    },
  };
};

/** Bands thinner than this clip their label, so it is dropped instead. */
const minLabelPct = 7;

const bandLabelBase: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: "1.2px",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  color: "rgba(255,255,255,0.92)",
  textShadow: "0 1px 2px rgba(0,0,0,0.5)",
};

const bandPctBase: React.CSSProperties = {
  fontWeight: 400,
  letterSpacing: 0,
  fontVariantNumeric: "tabular-nums",
  color: "rgba(255,255,255,0.7)",
};

/**
 * "up" and "down" run the label along a vertical band, reading away from the
 * detection area on either side; "flat" is the top/bottom band's normal text.
 */
type BandLabelVariant = "flat" | "up" | "down";

const ZoneBandLabel = ({
  pct,
  variant,
}: {
  pct: number;
  variant: BandLabelVariant;
}) => {
  const { t } = useTranslation(["dashboard", "common"]);
  if (pct < minLabelPct) return null;

  const flip = variant === "up" ? "rotate(180deg)" : undefined;
  const label: React.CSSProperties =
    variant === "flat"
      ? bandLabelBase
      : { ...bandLabelBase, writingMode: "vertical-rl", transform: flip };
  const pctStyle: React.CSSProperties =
    variant === "flat"
      ? bandPctBase
      : { ...bandPctBase, writingMode: "horizontal-tb", transform: flip };

  return (
    <span style={label}>
      {t("zone.notScanned")}
      <span style={pctStyle}>{formatPct(pct)}</span>
    </span>
  );
};

const detectionBadgeStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  borderRadius: "0 0 6px 6px",
  padding: "4px 12px",
  background: "rgba(32,169,99,0.92)",
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: "1px",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  color: "#fff",
};

const detectionBadgePctStyle: React.CSSProperties = {
  fontWeight: 400,
  letterSpacing: 0,
  fontVariantNumeric: "tabular-nums",
  color: "rgba(255,255,255,0.85)",
};

/** Names the band between the safe zones, tabbed onto its leading edge. */
const DetectionAreaBadge = ({
  direction,
  safeStartPct,
  safeEndPct,
}: {
  direction: DashboardConfigurationZoneDirectionEnum;
  safeStartPct: number;
  safeEndPct: number;
}) => {
  const { t } = useTranslation(["dashboard", "common"]);
  const start = clampPct(safeStartPct);
  const end = clampPct(safeEndPct);
  const anchor: React.CSSProperties = isHorizontalStrip(direction)
    ? { top: `${start}%`, left: 0, right: 0 }
    : { top: 0, left: `${start}%`, right: `${end}%` };

  return (
    <span
      style={{
        position: "absolute",
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
        ...anchor,
      }}
    >
      <span style={detectionBadgeStyle}>
        {t("zone.detectionArea")}
        <span style={detectionBadgePctStyle}>
          {formatPct(Math.max(0, 100 - start - end))}
        </span>
      </span>
    </span>
  );
};

const arrowIconForDirection: Record<
  DashboardConfigurationZoneDirectionEnum,
  LucideIcon
> = {
  [DashboardConfigurationZoneDirectionEnum.LEFT_TO_RIGHT]: ArrowRight,
  [DashboardConfigurationZoneDirectionEnum.RIGHT_TO_LEFT]: ArrowLeft,
  [DashboardConfigurationZoneDirectionEnum.TOP_TO_BOTTOM]: ArrowDown,
  [DashboardConfigurationZoneDirectionEnum.BOTTOM_TO_TOP]: ArrowUp,
};

export const ZoneArrow = ({
  direction,
  centerPct,
}: {
  direction: DashboardConfigurationZoneDirectionEnum;
  centerPct: number;
}) => {
  const Icon = arrowIconForDirection[direction];
  const horizontal = isHorizontalStrip(direction);
  const style: React.CSSProperties = {
    position: "absolute",
    top: horizontal ? `${centerPct}%` : "50%",
    left: horizontal ? "50%" : `${centerPct}%`,
    transform: "translate(-50%, -50%)",
    pointerEvents: "none",
    filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
  };
  return (
    <Icon
      className="size-8 text-white"
      strokeWidth={2.5}
      style={style}
      aria-hidden
    />
  );
};

/**
 * The full zone overlay for a live frame: both safe bands with their own
 * "not scanned" readout, the detection band's badge, and the travel arrow.
 * Absolutely positioned — the caller supplies a `relative` clipping box.
 */
export const SafeZoneOverlay = ({
  direction,
  centerPct,
  thicknessPct,
}: {
  direction: DashboardConfigurationZoneDirectionEnum;
  centerPct: number;
  thicknessPct: number;
}) => {
  const { safeStartPct, safeEndPct } = centerThicknessToSafeBounds(
    centerPct,
    thicknessPct,
  );
  const styles = getSafeZoneStyles(direction, safeStartPct, safeEndPct);
  const horizontalStrip = isHorizontalStrip(direction);

  return (
    <>
      <div style={styles.start}>
        <ZoneBandLabel
          pct={clampPct(safeStartPct)}
          variant={horizontalStrip ? "flat" : "up"}
        />
      </div>
      <div style={styles.end}>
        <ZoneBandLabel
          pct={clampPct(safeEndPct)}
          variant={horizontalStrip ? "flat" : "down"}
        />
      </div>
      <DetectionAreaBadge
        direction={direction}
        safeStartPct={safeStartPct}
        safeEndPct={safeEndPct}
      />
      <ZoneArrow direction={direction} centerPct={centerPct} />
    </>
  );
};

export { arrowIconForDirection };
